import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { 
  FileText, 
  Image as ImageIcon, 
  Loader2, 
  Clock, 
  ExternalLink,
  Plus,
  DollarSign,
  RefreshCw,
  Calendar,
  AlertCircle,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import PropertyInspectionDisplay from './PropertyInspectionDisplay';
import PropertyInspectionUpload from './PropertyInspectionUpload';
import PropertyAddInspectionPictures from './PropertyAddInspectionPictures';
import PropertyInspectionList from './PropertyInspectionList';
import InspectionImagesDisplay from './InspectionImagesDisplay';
import WorkItemComponent from '../WorkItemComponents/WorkItem';
import PropertyImageCarouselModal from './PropertyImageCarouselModal';
import propertyService from '../../services/propertyService';
import workAnalyzerService from '../../services/workAnalyzerService';
import { InspectionEvaluationResponse } from '../../models/inspectionEvaluationIssue';
import { WorkItem, Quote } from '../../models/workItem';
import { InspectionRecordStub } from '../../models/property';
import PropertyInspectionResultDisplay from './PropertyInspectionResultDisplay';

interface PropertyInspectionEvaluationProps {
  propertyId: string;
  inspectionEvaluation: InspectionEvaluationResponse | null; // Passed down from parent
  onInspectionDataLoaded: (inspectionData: string, isUpload?: boolean) => void; // Pass inspection data up
  inspectionId: string | null;
  workItem?: WorkItem; // Optional work item passed to this component
  address: string;
  onStateRefresh: () => Promise<void>; // Callback to trigger state refresh
  inspectionRecords: InspectionRecordStub[]; // All inspection records for this property
}

const PropertyInspectionEvaluation: React.FC<PropertyInspectionEvaluationProps> = ({
  propertyId,
  inspectionEvaluation,
  onInspectionDataLoaded,
  inspectionId,
  workItem,
  address,
  onStateRefresh, // Parent-provided callback
  inspectionRecords,
}) => {
  const [loading, setLoading] = useState(false);
  const [workItemLoading, setWorkItemLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState<'PDF' | 'Images' | null>(null);
  const [inspectionTypes, setInspectionTypes] = useState<Record<string, 'PDF' | 'Image' | undefined>>({});
  type TabType = 'latest' | 'history' | 'new';
  const [activeTab, setActiveTab] = useState<TabType>('latest');
  const location = useLocation();
  const navigate = useNavigate();
  
  // New state for tracking processing status
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState<string>('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [contextImages, setContextImages] = useState<string[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  
  // Extract the active view from URL parameters
  const getActiveSecondaryTab = (): 'issues' | 'estimate' => {
    const params = new URLSearchParams(location.search);
    return params.get('view') === 'estimate' ? 'estimate' : 'issues';
  };
  
  const activeSecondaryTab = getActiveSecondaryTab();
  
  // Calculate sorted inspections list
  const sortedInspections = React.useMemo(() => {
    if (!inspectionRecords || inspectionRecords.length === 0) return [];
    return [...inspectionRecords].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [inspectionRecords]);
  
  // Derived values
  const latestInspection = sortedInspections.length > 0 ? sortedInspections[0] : null;
  const isViewingLatest = inspectionId === latestInspection?.inspectionId;
  
  // Document URL state for PDF inspections
  const [inspectionDocumentUrls, setInspectionDocumentUrls] = useState<Record<string, string>>({});
  
  // Handler functions defined early
  const handleSelectInspection = async (selectedInspectionId: string) => {
    if (selectedInspectionId === inspectionId) return; // Already selected
    
    setLoading(true);
    try {
      // Pass false to indicate this is a selection, not an upload
      await onInspectionDataLoaded(selectedInspectionId, false);
      
      // Check inspection status and start polling if needed
      const status = await workAnalyzerService.getInspectionStatus(selectedInspectionId);
      
      // Determine if this is a status that needs polling
      const statesToPoll = ['PROCESSING', 'PAGES_GENERATED', 'ANALYZING', 'SYNTHESIZING', 'PENDING'];
      const terminalStates = ['COMPLETED', 'COMPLETED_WITH_ERRORS', 'SYNTHESIS_FAILED', 'FAILED', 'PARTIAL', 'ERROR'];
      const unknownStates = ['UNKNOWN', 'NULL', undefined, null];
      
      const shouldPoll = statesToPoll.includes(status) && !unknownStates.includes(status as any);
      const isTerminal = terminalStates.includes(status);
      
      if (shouldPoll) {
        // Start polling for status updates
        console.log(`Starting polling for inspection ${selectedInspectionId} with status: ${status}`);
        startPolling(selectedInspectionId);
      } else if (isTerminal) {
        // If it's a terminal state, set the status once but don't start polling
        setProcessingStatus(status);
        setProcessingMessage(getStatusMessage(status));
      }
    } catch (error) {
      console.error('Error loading selected inspection:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Get appropriate status message based on status code
  const getStatusMessage = (status: string | null): string => {
    if (!status) return 'Unknown status';
    
    switch (status) {
      case 'PROCESSING': return 'Preparing inspection for processing...';
      case 'PAGES_GENERATED': return 'Splitting document into pages...';
      case 'ANALYZING': return 'Analyzing inspection pages...';
      case 'SYNTHESIZING': return 'Combining analysis results...';
      case 'COMPLETED': return 'Processing complete!';
      case 'COMPLETED_WITH_ERRORS': return 'Processing completed with some errors.';
      case 'SYNTHESIS_FAILED': 
      case 'FAILED': return 'Processing failed. Please try again.';
      case 'PARTIAL': return 'Partial results available.';
      case 'UNKNOWN':
      case 'NULL':
      case 'ERROR': return 'Unknown status. Processing may have failed.';
      default: return `Current status: ${status}`;
    }
  };
  
  const handleInspectionUploaded = async (newInspectionId: string) => {
    setLoading(true);
    try {
      await onInspectionDataLoaded(newInspectionId); // Notify parent with the new inspection data
      
      // For new uploads, we always start polling immediately as they should begin in PROCESSING state
      console.log(`Starting polling for newly uploaded inspection ${newInspectionId}`);
      startPolling(newInspectionId);
      
      await onStateRefresh(); // Ensure state refresh after upload
    } catch (error) {
      console.error('Error refreshing after upload:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateWorkItem = async () => {
    setWorkItemLoading(true);
    try {
      if (inspectionId) {
        await propertyService.createWorkItemFromInspectionRecord(propertyId, inspectionId);
        console.log('Work item created successfully!');
        
        // Refresh state to reflect new work item
        await onStateRefresh(); 
        
        // Check if we're already on the estimate view
        const isAlreadyOnEstimateView = activeSecondaryTab === 'estimate';
        
        if (!isAlreadyOnEstimateView) {
          // Only navigate if we're not already on the estimate view
          // Use the navigate function with { replace: true } to avoid adding to history
          navigate(`/property/${propertyId}?tab=inspections&view=estimate`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error creating work item:', error);
    } finally {
      setWorkItemLoading(false);
    }
  };
  
  const handleAnalyzeWorkItem = (workItemId: string) => {
    console.log(`Analyzing Work Item with ID: ${workItemId}`); // Stubbed analyze function
  };
  
  // Function to handle document viewing
  const handleDocumentView = async (id: string) => {
    try {
      const documentUrl = await workAnalyzerService.getInspectionDocument(id);
      if (documentUrl) {
        window.open(documentUrl, '_blank');
      } else {
        console.error('Failed to retrieve document URL');
      }
    } catch (error) {
      console.error("Error fetching document URL:", error);
    }
  };

  // Determine inspection types (PDF or Image) and fetch document URLs
  useEffect(() => {
    if (inspectionRecords && inspectionRecords.length > 0) {
      const fetchInspectionData = async () => {
        const types: Record<string, 'PDF' | 'Image' | undefined> = {};
        const urls: Record<string, string> = {};
        
        // Process each inspection to determine its type and get document URL
        for (const record of inspectionRecords) {
          try {
            // Try to get document URL - if it exists, it's a PDF inspection
            const documentUrl = await workAnalyzerService.getInspectionDocument(record.inspectionId);
            
            if (documentUrl) {
              types[record.inspectionId] = 'PDF';
              urls[record.inspectionId] = documentUrl;
            } else {
              types[record.inspectionId] = 'Image';
            }
          } catch (error) {
            console.error(`Error determining type for inspection ${record.inspectionId}:`, error);
            // If we can't determine, assume Image (more common)
            types[record.inspectionId] = 'Image';
          }
        }
        
        setInspectionTypes(types);
        setInspectionDocumentUrls(urls);
      };
      
      fetchInspectionData();
    }
  }, [inspectionRecords]);
  
  // Auto-select latest inspection if none is selected
  useEffect(() => {
    if (!inspectionId && latestInspection && !loading) {
      const timer = setTimeout(() => {
        handleSelectInspection(latestInspection.inspectionId);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [inspectionId, latestInspection, loading]);
  
  // Check status on initial load or when inspection ID changes
  useEffect(() => {
    const checkCurrentInspectionStatus = async () => {
      if (!inspectionId) return;
      
      try {
        const status = await workAnalyzerService.getInspectionStatus(inspectionId);
        console.log(`Current inspection ${inspectionId} status: ${status}`);
        
        // Determine if this is a status that needs polling
        const statesToPoll = ['PROCESSING', 'PAGES_GENERATED', 'ANALYZING', 'SYNTHESIZING', 'PENDING'];
        const terminalStates = ['COMPLETED', 'COMPLETED_WITH_ERRORS', 'SYNTHESIS_FAILED', 'FAILED', 'PARTIAL', 'ERROR'];
        const unknownStates = ['UNKNOWN', 'NULL', undefined, null];
        
        const shouldPoll = statesToPoll.includes(status) && !unknownStates.includes(status as any);
        const isTerminal = terminalStates.includes(status);
        
        setProcessingStatus(status);
        setProcessingMessage(getStatusMessage(status));
        
        if (shouldPoll && !isPolling) {
          console.log(`Starting polling for inspection ${inspectionId} with status: ${status}`);
          startPolling(inspectionId);
        }
      } catch (error) {
        console.error('Error checking inspection status on load:', error);
      }
    };
    
    checkCurrentInspectionStatus();
  }, [inspectionId, isPolling]);
  
  // Reference to store the current polling promise
  const currentPollingRef = useRef<Promise<string> | null>(null);
  
  // Function to start polling for status updates
  const startPolling = (id: string) => {
    // If we're already polling, don't start another polling session
    if (isPolling && currentPollingRef.current) {
      console.log('Polling already in progress, not starting another one');
      return;
    }
    
    // Clean up any existing polling interval
    stopPolling();
    
    setIsPolling(true);
    setProcessingStatus('PROCESSING');
    setProcessingMessage('Processing inspection...');
    
    // Use the workAnalyzerService's poll function with callbacks
    const pollingPromise = workAnalyzerService.pollInspectionCompletion(id, {
      onStatusChange: (status) => {
        setProcessingStatus(status);
        
        // Update message based on status
        switch (status) {
          case 'PROCESSING':
            setProcessingMessage('Preparing inspection for processing...');
            break;
          case 'PAGES_GENERATED':
            setProcessingMessage('Splitting document into pages...');
            break;
          case 'ANALYZING':
            setProcessingMessage('Analyzing inspection pages...');
            break;
          case 'SYNTHESIZING':
            setProcessingMessage('Combining analysis results...');
            break;
          case 'COMPLETED':
            setProcessingMessage('Processing complete!');
            stopPolling();
            onStateRefresh(); // Refresh to get latest data
            break;
          case 'COMPLETED_WITH_ERRORS':
            setProcessingMessage('Processing completed with some errors.');
            stopPolling();
            onStateRefresh(); // Refresh to get latest data
            break;
          case 'SYNTHESIS_FAILED':
          case 'FAILED':
            setProcessingMessage('Processing failed. Please try again.');
            stopPolling();
            break;
          case 'PARTIAL':
            setProcessingMessage('Partial results available.');
            stopPolling();
            onStateRefresh(); // Refresh to get latest data
            break;
          case 'UNKNOWN':
          case 'NULL':
          case 'ERROR':
            setProcessingMessage('Unknown status. Processing may have failed.');
            stopPolling();
            break;
          default:
            setProcessingMessage(`Current status: ${status}`);
        }
      },
      initialDelay: 30000, // Start with 30 seconds
      maxDelay: 30000,   // Keep at 30 seconds
      maxAttempts: 60,   // Allow up to 60 attempts (30 minutes)
      terminateOn: [
        'COMPLETED', 
        'COMPLETED_WITH_ERRORS', 
        'SYNTHESIS_FAILED', 
        'FAILED', 
        'PARTIAL', 
        'ERROR', 
        'UNKNOWN', 
        'NULL'
      ]
    });
    
    // Store the current polling promise
    currentPollingRef.current = pollingPromise;
    
    pollingPromise.then(finalStatus => {
      // This callback will be executed when polling is complete
      console.log(`Polling complete with final status: ${finalStatus}`);
      setIsPolling(false);
      currentPollingRef.current = null;
    }).catch(error => {
      console.error('Error while polling for inspection status:', error);
      setProcessingMessage('Error checking status');
      setIsPolling(false);
      currentPollingRef.current = null;
    });
  };
  
  // Direct check inspection status (for one-time checks)
  const checkInspectionStatus = async (id: string) => {
    try {
      const status = await workAnalyzerService.getInspectionStatus(id);
      return status;
    } catch (error) {
      console.error('Error checking inspection status:', error);
      return 'ERROR';
    }
  };
  
  // Function to stop polling
  const stopPolling = () => {
    // Clean up any old setTimeout references (for backwards compatibility)
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    // Clear the polling promise reference
    currentPollingRef.current = null;
    
    // Update polling state
    setIsPolling(false);
  };
  
  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      // Clean up any timers
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
      }
      
      // Clear the promise reference
      currentPollingRef.current = null;
      setIsPolling(false);
    };
  }, []);
  
  // Load image URLs when context images change
  useEffect(() => {
    const loadContextImageUrls = async () => {
      if (contextImages.length === 0) return;
      
      try {
        // Use a local variable instead of state to track loading
        let isLoading = true;
        
        const urls: string[] = [];
        for (const imageId of contextImages) {
          try {
            const url = await propertyService.getPropertyImageUrl(propertyId, imageId);
            if (url) {
              urls.push(url);
            }
          } catch (error) {
            console.error(`Error fetching image URL for ${imageId}:`, error);
          }
        }
        
        setImageUrls(urls);
        
        // If we have at least one image URL, open the carousel
        if (urls.length > 0) {
          setCarouselOpen(true);
        } else {
          // If no images were loaded, show an error
          console.error('No image URLs could be loaded for the context');
        }
        
        isLoading = false;
      } catch (error) {
        console.error('Error loading context image URLs:', error);
      }
    };
    
    loadContextImageUrls();
  }, [contextImages, propertyId]);

  return (
    <div>
      {/* Global Loading Indicator */}
      {loading && (
        <div className="flex flex-col items-center my-8">
          <Loader2 className="h-10 w-10 text-primary animate-spin" />
          <p className="mt-4 text-muted-foreground">
            Loading inspection details...
          </p>
        </div>
      )}
      
      {/* Active polling indicator - minimal version */}
      {isPolling && !loading && (
        <div className="flex items-center gap-2 mb-2 px-4 py-2 text-xs text-blue-600">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Checking processing status every 30 seconds...</span>
        </div>
      )}

      {!loading && (
        <div>
          <div className="p-6 border-b">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              {/* Left side: Title area */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-medium">
                    {activeTab === 'latest' && 'Inspection Details'}
                    {activeTab === 'history' && 'Inspection History'}
                    {activeTab === 'new' && 'New Inspection'}
                  </h2>
                  
                </div>
                
                {activeTab === 'latest' && inspectionId && (
                  <div className="mt-2 space-y-2">
                    {/* Date Information */}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">
                        {inspectionId 
                          ? new Date(inspectionRecords.find(r => r.inspectionId === inspectionId)?.date || new Date()).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })
                          : 'Unknown'
                        }
                      </span>
                    </div>
                    
                    {/* Type Information */}
                    {inspectionTypes[inspectionId || ''] && (
                      <div className="flex items-center gap-2">
                        {inspectionTypes[inspectionId || ''] === 'PDF' ? 
                          <FileText className="h-4 w-4 text-gray-500" /> : 
                          <ImageIcon className="h-4 w-4 text-gray-500" />
                        }
                        <span className="text-gray-600">
                          {inspectionTypes[inspectionId || ''] === 'PDF' ? 'PDF Inspection Report' : 'Image-Based Inspection'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Right side: Actions area */}
              <div className="flex flex-col gap-3 md:items-end">
                {/* Tab navigation buttons */}
                <div className="flex items-center gap-2">
                  {activeTab !== 'latest' && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setActiveTab('latest')}
                      disabled={inspectionRecords.length === 0}
                      className="flex items-center gap-1"
                    >
                      <FileText className="h-4 w-4" />
                      <span>Current</span>
                    </Button>
                  )}
                  
                  {activeTab !== 'history' && (
                    <Button 
                      id="history-button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => setActiveTab('history')}
                      disabled={inspectionRecords.length <= 1}
                      className="flex items-center gap-1"
                    >
                      <Clock className="h-4 w-4" />
                      <span>History</span>
                    </Button>
                  )}
                  
                  {activeTab !== 'new' && (
                    <Button 
                      id="new-inspection-button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => setActiveTab('new')}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-4 w-4" />
                      <span>New</span>
                    </Button>
                  )}
                </div>
                
                {/* PDF link - only show for PDF inspections */}
                {activeTab === 'latest' && inspectionId && inspectionTypes[inspectionId] === 'PDF' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={() => handleDocumentView(inspectionId)}
                  >
                    <FileText className="h-4 w-4" />
                    <span>View Original PDF</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {activeTab === 'latest' && (
            <>
            {inspectionEvaluation ? (
              <div>
                
                {/* Inspection Content Area */}
                <div className="p-6">
                  {/* Toggle and Actions Header */}
                  <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    {/* Left side: Tab selection */}
                    <div className="inline-flex rounded-md border border-gray-200 bg-white shadow-sm">
                      <button
                        type="button"
                        className={`relative inline-flex items-center px-4 py-2 gap-2 text-sm font-medium rounded-l-md 
                          ${activeSecondaryTab === 'issues' 
                            ? 'bg-blue-50 text-fxer-blue z-10 ring-1 ring-fxer-blue ring-inset' 
                            : 'text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => {
                          // Use navigate instead of window.location for client-side routing
                          navigate(`/property/${propertyId}?tab=inspections&view=issues`);
                        }}
                      >
                        <FileText className={`h-4 w-4 ${activeSecondaryTab === 'issues' ? 'text-fxer-blue' : 'text-gray-500'}`} />
                        <span>Issues ({inspectionEvaluation.issues?.length || 0})</span>
                      </button>
                      <button
                        type="button"
                        className={`relative inline-flex items-center px-4 py-2 gap-2 text-sm font-medium rounded-r-md
                          ${activeSecondaryTab === 'estimate' 
                            ? 'bg-green-50 text-green-700 z-10 ring-1 ring-green-600 ring-inset' 
                            : 'text-gray-700 hover:bg-gray-50'}`}
                        onClick={() => {
                          // Use navigate instead of window.location for client-side routing
                          navigate(`/property/${propertyId}?tab=inspections&view=estimate`);
                        }}
                      >
                        <DollarSign className={`h-4 w-4 ${activeSecondaryTab === 'estimate' ? 'text-green-600' : 'text-gray-500'}`} />
                        <span>Estimate</span>
                      </button>
                    </div>
                    
                    {/* Right side: Action button */}
                    <div>
                      {workItem && workItem.inspectionId === inspectionId ? (
                        <Button
                          variant="outline"
                          onClick={handleCreateWorkItem}
                          disabled={workItemLoading}
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          {workItemLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4" />
                              <span>Regenerate Estimate</span>
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={handleCreateWorkItem}
                          disabled={workItemLoading}
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          {workItemLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <DollarSign className="h-4 w-4" />
                              <span>Generate Estimate</span>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Images for Image-Based Inspections - Only show on the issues view */}
                  {inspectionTypes[inspectionId || ''] === 'Image' && activeSecondaryTab === 'issues' && (
                    <div className="mb-6">
                      <h3 className="font-medium text-lg flex items-center gap-2 mb-3">
                        <ImageIcon className="h-5 w-5 text-purple-600" />
                        Inspection Images
                      </h3>
                      <InspectionImagesDisplay 
                        propertyId={propertyId}
                        inspectionId={inspectionId}
                      />
                    </div>
                  )}
                  
                  {/* Content area - Issues View */}
                  {activeSecondaryTab === 'issues' && (
                    <div>
                      <PropertyInspectionResultDisplay
                        inspectionEvaluation={inspectionEvaluation}
                        onCreateWorkItem={handleCreateWorkItem}
                        onOpenImageCarousel={(context) => {
                          // Filter context to ensure we only have valid image IDs (non-empty strings)
                          const validImageIds = context && Array.isArray(context) 
                            ? context.filter(id => typeof id === 'string' && id.trim() !== '')
                            : [];
                            
                          // Only proceed if we have at least one valid image ID
                          if (validImageIds.length > 0) {
                            setContextImages(validImageIds);
                            // Image loading and carousel opening will happen in the useEffect
                          } else {
                            console.warn('No valid image IDs found in context');
                          }
                        }}
                        showImages={inspectionTypes[inspectionId || ''] === 'Image'} // Only show images for Image type inspections
                        showWorkItemPrompt={false}
                      />
                    </div>
                  )}
                  
                  {/* Content area - Estimate View */}
                  {activeSecondaryTab === 'estimate' && (
                    <div>
                      
                      {workItem && workItem.inspectionId === inspectionId ? (
                        <div>
                          {workItem.createdDate && (
                            <div className="text-xs text-muted-foreground mb-4">
                              Generated: {new Date(workItem.createdDate).toLocaleString()}
                            </div>
                          )}
                          
                          <WorkItemComponent
                            workItem={workItem}
                            propertyId={propertyId}
                            quote={workItem.inspectionQuote}
                            onAnalyze={handleAnalyzeWorkItem}
                            address={address}
                          />
                        </div>
                      ) : (
                        <div className="p-8 text-center border border-dashed rounded-lg bg-gray-50">
                          <p className="text-muted-foreground mb-4">
                            No estimate has been generated for this inspection yet.
                          </p>
                          <Button onClick={handleCreateWorkItem}>
                            <DollarSign className="h-4 w-4 mr-2" />
                            <span>Generate Estimate</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="px-8 py-10 text-center border border-dashed rounded-lg bg-gray-50/70 mx-4">
                <p className="text-gray-500 mb-5">
                  {inspectionRecords.length > 0 
                    ? 'Select an inspection from the History tab' 
                    : 'No inspection reports yet'}
                </p>
                <Button 
                  onClick={() => setActiveTab('new')}
                  className="px-6 py-2 shadow-sm"
                >
                  <span className="font-medium">Add {inspectionRecords.length > 0 ? 'New' : 'First'} Inspection</span>
                </Button>
              </div>
            )}
            </>
          )}
          
          {activeTab === 'history' && (
            <PropertyInspectionList
              inspections={inspectionRecords}
              selectedInspectionId={inspectionId}
              onSelectInspection={(id) => {
                handleSelectInspection(id);
                setActiveTab('latest'); // Auto-switch to latest tab after selection
              }}
              inspectionTypes={inspectionTypes}
            />
          )}
          
          {activeTab === 'new' && (
            <div>
              {/* Type Selection */}
              <div className="mb-8 px-4">
                <h3 className="text-lg font-medium mb-5 text-gray-800">
                  Select inspection type:
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <Button
                    variant={uploadMode === 'PDF' ? 'default' : 'outline'}
                    onClick={() => setUploadMode('PDF')}
                    className="py-3 w-full h-auto shadow-sm"
                  >
                    <div className="flex flex-col items-center gap-3 py-2">
                      <FileText className="h-7 w-7" />
                      <span className="font-medium">PDF Report</span>
                    </div>
                  </Button>
                  <Button
                    variant={uploadMode === 'Images' ? 'default' : 'outline'}
                    onClick={() => setUploadMode('Images')}
                    className="py-3 w-full h-auto shadow-sm"
                  >
                    <div className="flex flex-col items-center gap-3 py-2">
                      <ImageIcon className="h-7 w-7" />
                      <span className="font-medium">Image Inspection</span>
                    </div>
                  </Button>
                </div>
              </div>
              
              {/* Upload Components */}
              {uploadMode && (
                <div className="mt-8 px-4">
                  {uploadMode === 'PDF' && (
                    <PropertyInspectionUpload 
                      propertyId={propertyId} 
                      onInspectionUploaded={(newId) => {
                        handleInspectionUploaded(newId);
                        setActiveTab('latest'); // Auto-switch to latest after upload
                      }} 
                    />
                  )}
                  {uploadMode === 'Images' && (
                    <PropertyAddInspectionPictures
                      propertyId={propertyId}
                      inspectionEvaluation={inspectionEvaluation}
                      inspectionId={inspectionId}
                    />
                  )}
                </div>
              )}
              
              {!uploadMode && (
                <div className="px-8 py-10 text-center border border-dashed rounded-lg bg-gray-50/70 mt-6 mx-4">
                  <p className="text-gray-500 text-sm">
                    Select an inspection type above to continue
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Image Carousel Modal for context images */}
      <PropertyImageCarouselModal
        imageUrls={imageUrls}
        isOpen={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        initialIndex={0}
        imageInfos={contextImages.map(id => ({
          // We don't have room or description info for context images
          // But we can create placeholder info objects
          room: "Inspection Image",
          description: "Image related to inspection issue",
          redFlags: [] // No red flags to display
        }))}
        roomName="Inspection Context"
      />
    </div>
  );
};

export default PropertyInspectionEvaluation;
